import { CheckoutContext } from '@/context/CheckoutContext';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { useContext, useMemo } from 'react';
import { shouldHideXButton } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { useDetailsContentColor } from '@/hooks/useDetailsContentColor';
import { cn } from '@/utils/cn';
import { useCancelCheckout } from '@/hooks/useCancelCheckout';
import { isRedirectModeRequested } from '@/utils/checkoutMode';
import { useIsMobile } from '@/hooks/useIsMobile';
import { hasPublisherHaveBackgroundImage } from '@/utils/publisher';

export const XButton = ({ onClick, style }: { onClick: () => void; style?: React.CSSProperties }) => {
  const context = useContext(CheckoutContext);
  const { featureFlags, boot, template } = context;
  const isMobile = useIsMobile();
  const hasBackgroundImage = hasPublisherHaveBackgroundImage(boot?.checkoutTheme, template.type);
  // On mobile the X mirrors the Details text color. On desktop it lives in the
  // white payment column away from Details, so it keeps its own default styling.
  const detailsColor = useDetailsContentColor();
  const { t } = useTranslate();
  const { cancelAndClose, isClosingRef, isPaymentInFlight } = useCancelCheckout(onClick);
  // Only `?mode=redirect` removes the button — Payment Links keeps its X and
  // the `status=cancel` it reports. Redirect mode offers `BackToStoreLink`
  // instead, which reports the same status through the same hook.
  const isRedirectMode = isRedirectModeRequested();
  const showButton = useMemo(() => {
    return !shouldHideXButton(featureFlags, isRedirectMode);
  }, [featureFlags, isRedirectMode]);

  return showButton ? (
    <div className="relative h-5 md:mt-0 mt-0 w-full md:h-0 landscape:h-0 landscape:mt-0" style={style}>
      <button
        data-testid="close-checkout-button"
        onClick={cancelAndClose}
        disabled={isClosingRef.current || isPaymentInFlight}
        className={cn(
          'absolute top-2 right-2 md:top-6 md:right-6 w-6 h-6 flex items-center justify-center cursor-pointer rounded-full bg-white transition-all duration-200 z-50 pr-2',
          hasBackgroundImage && 'bg-transparent',
          isPaymentInFlight && 'invisible pointer-events-none'
        )}
        style={isMobile ? detailsColor : undefined}
        aria-hidden={isPaymentInFlight}
        aria-label={t('closeCheckout')}
      >
        <FontAwesomeIcon icon="fa-solid fa-xmark" />
      </button>
    </div>
  ) : null;
};
